// Upload NAV Dialog - Upload IB Flex Query XML to update NAV
import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { CloudUpload, CheckCircle, Warning } from '@mui/icons-material';
import {
  parseIBFlexQueryXML,
  validateIBFlexQueryXML,
  formatIBFlexQueryData,
  IBFlexQueryData,
} from '../../services/ibFlexQueryParser';
import {
  updatePoolNAVFromFlexQuery,
  validateNAVUpdate,
} from '../../services/navUpdateService';
import { formatCurrency } from '../../utils/navCalculations';

interface UploadNAVDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const UploadNAVDialog: React.FC<UploadNAVDialogProps> = ({ open, onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [parsedData, setParsedData] = useState<IBFlexQueryData | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = async (selectedFile: File) => {
    setError('');
    setWarning('');
    setParsedData(null);
    setUploadSuccess(false);

    // Validate file type
    if (!selectedFile.name.endsWith('.xml')) {
      setError('Please upload an XML file');
      return;
    }

    setFile(selectedFile);
    setLoading(true);

    try {
      // Read file content
      const text = await selectedFile.text();

      // Validate XML
      const validation = validateIBFlexQueryXML(text);
      if (!validation.valid) {
        throw new Error(validation.error || 'Invalid XML file');
      }

      // Parse XML
      const data = parseIBFlexQueryXML(text);
      setParsedData(data);

      // Validate NAV update
      const navValidation = await validateNAVUpdate(data.endingNAV, data.date);
      if (!navValidation.valid) {
        throw new Error(navValidation.warning || 'Invalid NAV update');
      }
      if (navValidation.warning) {
        setWarning(navValidation.warning);
      }

    } catch (err: any) {
      setError(err.message || 'Failed to parse XML file');
      setFile(null);
      setParsedData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleConfirmUpload = async () => {
    if (!parsedData) return;

    setLoading(true);
    setError('');

    try {
      const result = await updatePoolNAVFromFlexQuery(parsedData);

      if (!result.success) {
        throw new Error(result.error || 'Failed to update NAV');
      }

      setUploadSuccess(true);
      
      // Wait a moment to show success message
      setTimeout(() => {
        if (onSuccess) onSuccess();
        handleClose();
      }, 1500);

    } catch (err: any) {
      setError(err.message || 'Failed to update NAV');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFile(null);
      setParsedData(null);
      setError('');
      setWarning('');
      setUploadSuccess(false);
      onClose();
    }
  };

  const changePercent = parsedData && parsedData.startingNAV !== 0
    ? ((parsedData.mtmChange / parsedData.startingNAV) * 100)
    : 0;
  const changeColor = parsedData && parsedData.mtmChange >= 0 ? '#4CAF50' : '#F44336';

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1a1a',
          border: '1px solid #FFD700',
        }
      }}
    >
      <DialogTitle sx={{ color: '#FFD700', borderBottom: '1px solid #333' }}>
        Upload NAV from IB Flex Query
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {warning && !error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {warning}
          </Alert>
        )}

        {uploadSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
            NAV updated successfully! Refreshing dashboard...
          </Alert>
        )}

        {!parsedData && !uploadSuccess && (
          <Box
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            sx={{
              border: dragActive ? '2px dashed #FFD700' : '2px dashed #666',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              bgcolor: dragActive ? 'rgba(255, 215, 0, 0.05)' : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                borderColor: '#FFD700',
                bgcolor: 'rgba(255, 215, 0, 0.05)',
              }
            }}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <CloudUpload sx={{ fontSize: 64, color: '#FFD700', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
              {file ? file.name : 'Drag & Drop XML File Here'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              or click to browse
            </Typography>
            <input
              id="file-input"
              type="file"
              accept=".xml"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            {loading && <CircularProgress sx={{ mt: 2, color: '#FFD700' }} />}
          </Box>
        )}

        {parsedData && !uploadSuccess && (
          <Box sx={{ p: 2, bgcolor: '#0a0a0a', borderRadius: 1, border: '1px solid #FFD700' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#FFD700' }}>
                NAV Data Preview
              </Typography>
              <Chip
                label={file?.name}
                onDelete={() => {
                  setFile(null);
                  setParsedData(null);
                  setWarning('');
                }}
                sx={{ bgcolor: '#333', color: 'white' }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#999' }}>Account:</Typography>
                <Typography sx={{ color: 'white', fontWeight: 'bold' }}>{parsedData.accountId}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#999' }}>Date:</Typography>
                <Typography sx={{ color: 'white' }}>{parsedData.date.toLocaleDateString()}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#999' }}>Starting NAV:</Typography>
                <Typography sx={{ color: 'white' }}>{formatCurrency(parsedData.startingNAV)}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#999' }}>Ending NAV:</Typography>
                <Typography sx={{ color: '#FFD700', fontWeight: 'bold', fontSize: '1.2rem' }}>
                  {formatCurrency(parsedData.endingNAV)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #333' }}>
                <Typography sx={{ color: '#999' }}>Change:</Typography>
                <Typography sx={{ color: changeColor, fontWeight: 'bold' }}>
                  {parsedData.mtmChange >= 0 ? '+' : ''}
                  {formatCurrency(parsedData.mtmChange)} ({parsedData.mtmChange >= 0 ? '+' : ''}{changePercent.toFixed(2)}%)
                </Typography>
              </Box>
            </Box>

            {warning && (
              <Alert severity="warning" sx={{ mt: 2 }} icon={<Warning />}>
                {warning}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #333', p: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          sx={{ color: 'white' }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirmUpload}
          disabled={loading || !parsedData || uploadSuccess}
          variant="contained"
          sx={{
            bgcolor: '#FFD700',
            color: '#000',
            '&:hover': { bgcolor: '#FFC700' },
            '&:disabled': { bgcolor: '#555', color: '#999' },
          }}
        >
          {loading ? <CircularProgress size={24} /> : 'Confirm & Update NAV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadNAVDialog;
